import ReactGA from 'react-ga4';

import { getSampleSuffix } from '../utils';

// GENERAL

export const trackClickDoneButton = (
  page: string,
  label: string,
  isSample: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_click_done_button${getSampleSuffix(isSample)}`,
    label,
  });
};

export const trackClickMainBackButton = (page: string, isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_click_main_back_button${getSampleSuffix(isSample)}`,
  });
};

export const trackClickBackButton = (page: string, isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_click_back_button${getSampleSuffix(isSample)}`,
  });
};

export const trackClickTakeMeBack = (page: string, isSample?: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_click_take_me_back${getSampleSuffix(isSample ?? false)}`,
  });
};

export const trackClickOpenMAPage = (page: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_click_open_missing_assets_page`,
  });
};

// UPLOAD

export const trackUploadSample = (page: string, count?: number) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_${!!count ? 'batch_' : ''}upload_success-[Sample]`,
    value: count ?? 1,
  });
};

export const trackUploadFileSuccess = (
  page: string,
  eventId: string,
  duration?: number,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_upload_success`,
    label: eventId,
    value: duration,
  });
};

export const trackUploadBatchSuccess = (
  page: string,
  eventId: string,
  duration?: number,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_batch_upload_success`,
    label: eventId,
    value: duration,
  });
};

export const trackUploadError = (page: string, fileName: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_upload_error`,
    label: fileName,
  });
};

export const trackUploadOrientationChoiceAccept = (
  page: string,
  orientation: string,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_orientation_choice_accept`,
    label: orientation,
  });
};

export const trackUploadOrientationReminderAccept = (
  page: string,
  isSaved: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_orientation_reminder_accept`,
    label: `${isSaved ? '' : 'NOT '} Saved`,
  });
};

export const trackUploadOrientationModalReject = (
  page: string,
  isReminder: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_orientation_${isReminder ? 'reminder' : 'choice'}_reject`,
  });
};

export const trackUploadShotReminderAccept = (isSaved: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'hl_reel_shot_reminder_accept',
    label: `${isSaved ? '' : 'NOT '} Saved`,
  });
};

export const trackUploadShotReminderReject = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'hl_reel_shot_reminder_reject',
  });
};

export const trackDownloadShotImage = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'hl_reel_download_shot_image',
  });
};

export const trackDownloadIndividualSamples = (page: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_download_samples`,
  });
};

export const trackClickUploadMore = (
  page: string,
  position: string,
  isSample: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `${page}_click_upload_more${getSampleSuffix(isSample)}`,
    label: position,
  });
};

export const trackVBSaveRemindLaterSuccess = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_save_remind_later_success',
  });
};

export const trackVBSaveRemindLaterError = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_save_remind_later_error',
  });
};

// Assign

export const trackVBAssignModalOpen = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_open',
  });
};

export const trackVBAssignModalAddAssignee = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_add_assignee',
  });
};

export const trackVBAssignModalRemoveAssignee = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_remove_assignee',
  });
};

export const trackVBAssignModalChangeAssess = (access: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_change_access',
    label: `To ${access} access`,
  });
};

export const trackVBAssignModalOpenLink = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_open_link',
  });
};

export const trackVBAssignModalCopyLink = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_copy_link',
  });
};

export const trackVBAssignModalSaveSuccess = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_save_success',
  });
};

export const trackVBAssignModalSaveError = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'vb_assign_modal_save_error',
  });
};

// People

export const trackPeopleClickCheckInLink = (source: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_click_check_in_link',
    label: source,
  });
};

export const trackPeopleToggleUseFileName = (isToUseFileName: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_click_use_filename_toggle',
    label: isToUseFileName ? 'To use filename' : 'To not use filename',
  });
};

export const trackPeopleToggleDetails = (isToDetails: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_click_details_toggle',
    label: isToDetails ? 'To details' : 'To summary',
  });
};

export const trackPeopleDeletePerson = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_delete_person',
  });
};

export const trackPeopleEditPerson = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_click_edit_person',
  });
};

export const trackPeopleUpdatePersonSuccess = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_update_person_success',
  });
};

export const trackPeopleUpdatePersonError = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_update_person_error',
  });
};

export const trackPeopleUploadAdditionalPhotosSuccess = (count: number) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_upload_additional_photos_success',
    value: count,
  });
};

export const trackPeopleUploadAdditionalPhotosError = (fileName: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'people_upload_additional_photos_error',
    label: fileName,
  });
};

// Clips

export const trackClipsDeleteClip = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'clips_delete_clip',
  });
};

// ANALYSIS

export const trackAnalysisStartSuccess = (
  isQuickStart: boolean,
  isSample: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `analysis_start_success${getSampleSuffix(isSample)}`,
    label: isQuickStart ? 'Upload' : 'Video Builder',
  });
};

export const trackAnalysisStartError = (errMsg: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_start_error',
    label: errMsg,
  });
};

export const trackAnalysisClickSeeProgress = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_click_see_progress',
  });
};

export const trackAnalysisFinish = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `analysis_finish${getSampleSuffix(isSample)}`,
  });
};

export const trackAnalysisShowClipsWarning = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `analysis_show_clips_warning${getSampleSuffix(isSample)}`,
  });
};

export const trackAnalysisClickOnPerson = (
  label: string,
  isSample: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `analysis_click_on_person${getSampleSuffix(isSample)}`,
    label,
  });
};

export const trackAnalysisCreationPreviewPlay = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `analysis_creation_preview_play${getSampleSuffix(isSample)}`,
  });
};

export const trackAnalysisCreationPreviewError = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `analysis_creation_preview_error${getSampleSuffix(isSample)}`,
  });
};

export const trackAnalysisOpenHub = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_open_hub',
  });
};

export const trackAnalysisOpenCreation = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_open_creation',
  });
};

export const trackAnalysisClickUploadMoreClips = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_click_upload_more_clips',
  });
};

export const trackAnalysisClickVBInNewUploads = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_click_vb_in_new_uploads',
  });
};

export const trackAnalysisDeleteClip = (label: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'analysis_delete_clip',
    label,
  });
};

// GENERATE

export const trackGenerationStartSuccess = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `generation_start_success${getSampleSuffix(isSample)}`,
  });
};

export const trackGenerationStartError = (errMsg: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'generation_start_error',
    label: errMsg,
  });
};

export const trackGenerationFinish = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `generation_finish${getSampleSuffix(isSample)}`,
  });
};

export const trackAttendeeClickGenerate = () => {
  ReactGA.event({
    category: 'Attendee',
    action: 'attendee_click_generate',
  });
};

export const trackAttendeeGenerationStartSuccess = () => {
  ReactGA.event({
    category: 'Attendee',
    action: 'attendee_generation_start_success',
  });
};

export const trackAttendeeGenerationStartError = (errMsg: string) => {
  ReactGA.event({
    category: 'Attendee',
    action: 'attendee_generation_start_error',
    label: errMsg,
  });
};

export const trackAttendeeGenerationFinish = () => {
  ReactGA.event({
    category: 'Attendee',
    action: 'attendee_generation_finish',
  });
};

// SHARE

export const trackShareOpenShareHubModal = (isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `share_open_share_hub_modal${getSampleSuffix(isSample)}`,
  });
};

export const trackShareOpenLink = (type: string, isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `share_open_${type}_link${getSampleSuffix(isSample)}`,
  });
};

export const trackShareCopyLink = (type: string, isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `share_copy_${type}_link${getSampleSuffix(isSample)}`,
  });
};

export const trackShareDownload = (type: string, isSample: boolean) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `share_download_${type}${getSampleSuffix(isSample)}`,
  });
};

export const trackShareToggleVideoSize = (
  isToIncreased: boolean,
  isSample: boolean,
) => {
  ReactGA.event({
    category: 'Video Builder',
    action: `share_toggle_video_size${getSampleSuffix(isSample)}`,
    label: isToIncreased ? 'Increased' : 'Reduced',
  });
};

export const trackShareBulkShare = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'share_click_bulk_share',
  });
};

export const trackShareSampleShare = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'share_click_share-[Sample]',
  });
};

export const trackShareCreationShare = (label: string) => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'share_click_creation_share',
    label,
  });
};

export const trackShareReOpenWorkflow = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'share_click_re_open_workflow',
  });
};

// SET DATE (AR FLOW)

export const trackSetDateUpdateSuccess = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'set_date_update_success',
  });
};

export const trackSetDateUpdateError = () => {
  ReactGA.event({
    category: 'Video Builder',
    action: 'set_date_update_error',
  });
};
